import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { API, LicenseDTO, LicenseType, UserFullDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { ActivatedRoute } from '@angular/router';
import { LicenceManagementGenerateModalComponent } from './modals/generate-licences/licence-management-generate-modal.component';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { LicenceManagementCopySerialModalComponent } from './modals/copy-serial/licence-management-copy-serial-modal.component';
import { SbClipboardListInputModalComponent } from '../../../components/sb-clipboard-list-input-modal/sb-clipboard-list-input-modal.component';
import { LicenceManagementAutoAssignModalComponent } from './modals/auto-assign/auto-assign-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';

type SplitButtonAction = 'unassign' | 'assign' | 'copy' | 'delete';

@Component({
  selector: 'sb-page-admin-client-licence-management',
  templateUrl: './admin-client-licences.component.html',
  styleUrls: ['./admin-client-licences.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminClientLicencesComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly errorHandler = inject(ErrorService);
  private readonly api = inject(API);
  private readonly modal = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToasterService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  @Input() clientID: number;

  // table wrapper variables
  public licences: LicenseDTO[];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  public refreshFromServer(): void {
    if (!this.pagination || !this.clientID) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      ._admin_License_retrieveClientLicenses(
        this.clientID,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (licences: LicenseDTO[]) => {
          this.licences = this.buildSplitButtons(licences);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  public openGenerateLicencesModal(): void {
    this.modal
      .showNavigationModal({
        entryComponent: { value: LicenceManagementGenerateModalComponent, data: { clientID: this.clientID } },
        title: this.translate.instant('lang.admin.licenceManagement.create.plural'),
        icon: 'plus',
        modalOptions: { windowClass: 'M' },
      })
      .then((data: { type: LicenseType; count: number }) => {
        if (data.type && data.count > 0) {
          this.loading.set(true);
          this.api
            ._admin_License_createLicenses(this.clientID, data.type, data.count)
            .pipe(
              take(1),
              finalize(() => {
                this.loading.set(false);
              })
            )
            .subscribe({
              next: () => {
                if (data.count < 2) {
                  this.toastService.toast(
                    ToastType.SUCCESS,
                    this.translate.instant('lang.admin.licenceManagement.created.singular', { count: data.count })
                  );
                } else {
                  this.toastService.toast(
                    ToastType.SUCCESS,
                    this.translate.instant('lang.admin.licenceManagement.created.plural', { count: data.count })
                  );
                }
                this.refreshFromServer();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        this.requestDebounced();
      })
      .catch(() => {
        this.requestDebounced();
      });
  }

  public downloadCSV(): void {
    this.loading.set(true);
    const sub = this.api
      ._admin_License_retrieveClientLicensesAsCSV(this.clientID)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: downloadUrl => {
          window.open(downloadUrl);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  public openLoadLicencesModal(): void {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbClipboardListInputModalComponent, data: {} },
        title: this.translate.instant('lang.admin.licenceManagement.load'),
        icon: 'upload',
        modalOptions: { windowClass: 'M' },
      })
      .then((serials: string[]) => {
        if (serials.length > 0) {
          this.loading.set(true);
          this.api
            ._admin_License_loadLicenses(this.clientID, { serials })
            .pipe(
              take(1),
              finalize(() => {
                this.loading.set(false);
              })
            )
            .subscribe({
              next: (loadedLicences: LicenseDTO[]) => {
                const count = loadedLicences.length;
                this.refreshFromServer();
                if (count < 2) {
                  this.toastService.toast(
                    ToastType.SUCCESS,
                    this.translate.instant('lang.admin.licenceManagement.loaded.singular', { count })
                  );
                } else {
                  this.toastService.toast(
                    ToastType.SUCCESS,
                    this.translate.instant('lang.admin.licenceManagement.loaded.plural', { count })
                  );
                }
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(error => {
        this.loading.set(false);
        this.errorHandler.errorHandler(error);
      });
  }

  public autoAssignAll(): void {
    this._openAutoAssignModal().then(result => {
      this.loading.set(true);
      this.api
        ._admin_License_autoAssignLicenses(this.clientID, result)
        .pipe(
          take(1),
          finalize(() => {
            this.loading.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.refreshFromServer();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  public autoAssign(licence?: LicenseDTO): void {
    this._openAutoAssignModal().then(result => {
      this.loading.set(true);
      this.api
        ._admin_License_autoAssignById(this.clientID, licence.id, result)
        .pipe(
          take(1),
          finalize(() => {
            this.loading.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.refreshFromServer();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  public unAssign(licence?: LicenseDTO): void {
    this.modal
      .confirm({
        title: this.translate.instant('lang.admin.licenceManagement.unassign'),
        message: this.translate.instant('lang.admin.licenceManagement.unassign.infoText'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then(result => {
        if (result) {
          this.loading.set(true);
          this.api
            ._admin_License_unassignById(this.clientID, licence.id)
            .pipe(
              take(1),
              finalize(() => {
                this.loading.set(false);
              })
            )
            .subscribe({
              next: (unassigned: boolean) => {
                if (unassigned) {
                  this.refreshFromServer();
                  this.toastService.toast(ToastType.SUCCESS, this.translate.instant('lang.admin.licenceManagement.unassigned'));
                }
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  private _openAutoAssignModal(): Promise<{
    disabled: boolean;
    productive: boolean;
    machineCode: string[];
  }> {
    return new Promise((resolve, reject) => {
      this.modal
        .showNavigationModal({
          entryComponent: { value: LicenceManagementAutoAssignModalComponent, data: {} },
          title: this.translate.instant('lang.admin.licenceManagement.autoAssign'),
          icon: 'link',
          modalOptions: { windowClass: 'M' },
        })
        .then((response: { disabled: boolean; productive: boolean; machineCode: string[] }) => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public copySerial(licence: LicenseDTO): void {
    this.modal
      .showNavigationModal({
        entryComponent: { value: LicenceManagementCopySerialModalComponent, data: { serial: licence.serial } },
        title: this.translate.instant('lang.admin.licenceManagement.copySerial'),
        icon: 'copy',
        modalOptions: { windowClass: 'M' },
      })
      .then(() => {})
      .catch(() => {});
  }

  public delete(licence: LicenseDTO): void {
    if (licence) {
      this.modal
        .confirm({
          title: this.translate.instant('lang.admin.licenceManagement.delete'),
          message: this.translate.instant('lang.admin.licenceManagement.delete.message'),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.loading.set(true);
            this.api
              ._admin_License_delete(this.clientID, licence.id)
              .pipe(
                take(1),
                finalize(() => {
                  this.loading.set(false);
                })
              )
              .subscribe({
                next: () => {
                  this.refreshFromServer();
                  this.toastService.toast(ToastType.SUCCESS, this.translate.instant('lang.admin.licenceManagement.deleted'));
                  this.copySerial(licence);
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          }
        });
    }
  }

  unAssignAll() {
    this.modal
      .confirm({
        title: this.translate.instant('lang.admin.licenceManagement.unassignAll'),
        message: this.translate.instant('lang.admin.licenceManagement.unassignAll.infoText'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then(result => {
        if (result) {
          this.loading.set(true);
          this.api
            ._admin_License_unassignAll(this.clientID)
            .pipe(
              take(1),
              finalize(() => {
                this.loading.set(false);
              })
            )
            .subscribe({
              next: (unassigned: boolean) => {
                if (unassigned) {
                  this.refreshFromServer();
                  this.toastService.toast(ToastType.SUCCESS, this.translate.instant('lang.admin.licenceManagement.unassigned'));
                }
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  buildSplitButtons<T extends { id: any; inUse: boolean }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return {
        ...item,
        sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
        extraSbButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
      };
    });
    result.forEach(item => {
      if (item?.inUse) {
        item.extraSbButtons.push({
          icon: 'linkNone',
          action: 'unassign',
          tooltip: 'lang.admin.licenceManagement.unassign',
        });
      } else {
        item.extraSbButtons.push({
          icon: 'link',
          iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
          action: 'assign',
          tooltip: 'lang.admin.licenceManagement.autoAssign.action',
        });
      }
      item.sbButtons.push({
        icon: 'copy',
        action: 'copy',
        tooltip: 'lang.admin.licenceManagement.copySerial',
      });
      item.sbButtons.push({
        icon: 'trash',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.globals.delete',
      });
    });
    return result;
  }

  resolveSplitButtonAction(license: LicenseDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'unassign':
        this.unAssign(license);
        break;
      case 'assign':
        this.autoAssign(license);
        break;
      case 'copy':
        this.copySerial(license);
        break;
      case 'delete':
        this.delete(license);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
